import {useEffect, useCallback, useRef} from 'react';
import {
  ACTIVE_HIGHLIGHT_STYLE,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_TYPES,
} from '../constants';

const FOOTING_ERROR_BACKGROUND =
  HIGHLIGHT_COLORS[HIGHLIGHT_TYPES.FOOTING].error.background;
const FOOTING_CORRECT_BACKGROUND =
  HIGHLIGHT_COLORS[HIGHLIGHT_TYPES.FOOTING].correct.background;
const FOOTING_ACTIVE_BORDER = ACTIVE_HIGHLIGHT_STYLE.border;
const FOOTING_ACTIVE_BOX_SHADOW = ACTIVE_HIGHLIGHT_STYLE.boxShadow;

const applyActiveStyle = (node) => {
  node.style.border = FOOTING_ACTIVE_BORDER;
  node.style.boxShadow = FOOTING_ACTIVE_BOX_SHADOW;
};

const removeActiveStyle = (node) => {
  node.style.border = 'none';
  node.style.boxShadow = 'none';
};

const createFootingNode = ({width, height, isCorrect}) => {
  const node = document.createElement('div');

  const backgroundColor = isCorrect
    ? FOOTING_CORRECT_BACKGROUND
    : FOOTING_ERROR_BACKGROUND;

  // CRITICAL: pointer-events: none allows all mouse events to pass through
  node.style.cssText = `
    width: ${width}px;
    height: ${height}px;
    background-color: ${backgroundColor};
    mix-blend-mode: multiply;
    border: none;
    box-shadow: none;
    box-sizing: border-box;
    pointer-events: none;
    transition: border 0.15s ease, box-shadow 0.15s ease;
    user-select: none;
  `;

  return node;
};

const normalizeBboxToPageCoords = (bbox, pageWidth, pageHeight) => {
  return {
    x: bbox.left * pageWidth,
    y: bbox.top * pageHeight,
    width: (bbox.right - bbox.left) * pageWidth,
    height: (bbox.bottom - bbox.top) * pageHeight,
  };
};

export const useFootingOverlays = ({instance, NutrientViewer}) => {
  const overlayIdsRef = useRef(new Set());
  const footingDataRef = useRef(new Map());
  const activeFootingIdRef = useRef(null);

  const getFootingAtPoint = useCallback(
    (pageIndex, point) => {
      if (!instance) return null;

      for (const [, data] of footingDataRef.current.entries()) {
        if (data.pageIndex !== pageIndex) continue;

        const {footing} = data;
        const {box} = footing.cell_grounding ?? {};
        if (!box) continue;

        const pageInfo = instance.pageInfoForIndex(pageIndex);
        if (!pageInfo) continue;

        const coords = normalizeBboxToPageCoords(
          box,
          pageInfo.width,
          pageInfo.height
        );

        if (
          point.x >= coords.x &&
          point.x <= coords.x + coords.width &&
          point.y >= coords.y &&
          point.y <= coords.y + coords.height
        ) {
          return footing;
        }
      }
      return null;
    },
    [instance]
  );

  const clearFootingOverlays = useCallback(() => {
    if (!instance) return;

    overlayIdsRef.current.forEach((id) => {
      try {
        instance.removeCustomOverlayItem(id);
      } catch (err) {
        console.warn(
          `[useFootingOverlays] Could not remove overlay ${id}:`,
          err.message
        );
      }
    });

    overlayIdsRef.current.clear();
    footingDataRef.current.clear();
    activeFootingIdRef.current = null;
  }, [instance]);

  const showFootingOverlays = useCallback(
    (footings, activeFootingId) => {
      if (!instance || !NutrientViewer || !footings?.length) {
        return;
      }

      clearFootingOverlays();

      footings.forEach((footing) => {
        const {page: pageIndex, box} = footing.cell_grounding ?? {};

        if (!box || pageIndex == null) {
          console.warn(
            '[useFootingOverlays] Footing missing bbox or page:',
            footing.id
          );
          return;
        }

        const pageInfo = instance.pageInfoForIndex(pageIndex);
        if (!pageInfo) {
          console.warn(
            '[useFootingOverlays] Could not get page info for page:',
            pageIndex
          );
          return;
        }

        const coords = normalizeBboxToPageCoords(
          box,
          pageInfo.width,
          pageInfo.height
        );
        const isActive = footing.id === activeFootingId;

        const node = createFootingNode({
          width: coords.width,
          height: coords.height,
          isCorrect: footing.is_footing_correct,
        });

        if (isActive && !footing.is_footing_correct) {
          applyActiveStyle(node);
        }

        const overlayId = `footing-${footing.id}`;

        const overlayItem = new NutrientViewer.CustomOverlayItem({
          id: overlayId,
          node,
          pageIndex,
          position: new NutrientViewer.Geometry.Point({
            x: coords.x,
            y: coords.y,
          }),
          onAppear: () => {
            // We need to disable pointer-events on the grandparent of the node to allow the mouse events to pass through to the page
            // so that double clicking on a footing will open the inlineTextSelectionToolbar for the commenting
            const grandparent = node.parentElement?.parentElement;
            if (grandparent) {
              grandparent.style.pointerEvents = 'none';
            }
          },
        });

        instance.setCustomOverlayItem(overlayItem);

        overlayIdsRef.current.add(overlayId);
        footingDataRef.current.set(footing.id, {
          overlayId,
          pageIndex,
          footing,
          node,
        });
      });

      activeFootingIdRef.current = activeFootingId;
      console.log(
        `[useFootingOverlays] Created ${overlayIdsRef.current.size} overlays`
      );
    },
    [instance, NutrientViewer, clearFootingOverlays]
  );

  const setActiveFooting = useCallback((footingId) => {
    const previousActiveId = activeFootingIdRef.current;
    if (previousActiveId === footingId) return;

    if (previousActiveId) {
      const prevData = footingDataRef.current.get(previousActiveId);
      if (prevData?.node) {
        removeActiveStyle(prevData.node);
      }
    }

    if (footingId) {
      const newData = footingDataRef.current.get(footingId);
      if (newData?.node && !newData.footing?.is_footing_correct) {
        applyActiveStyle(newData.node);
      }
    }

    activeFootingIdRef.current = footingId;
  }, []);

  const scrollToFooting = useCallback(
    (footing) => {
      if (!instance || !NutrientViewer || !footing) return;

      const {page: pageIndex, box} = footing.cell_grounding ?? {};

      if (pageIndex == null) return;

      if (!box) {
        instance.setViewState((viewState) =>
          viewState.set('currentPageIndex', pageIndex)
        );
        return;
      }

      const pageInfo = instance.pageInfoForIndex(pageIndex);
      if (!pageInfo) return;

      const coords = normalizeBboxToPageCoords(
        box,
        pageInfo.width,
        pageInfo.height
      );

      const rect = new NutrientViewer.Geometry.Rect({
        left: coords.x,
        top: coords.y,
        width: coords.width,
        height: coords.height,
      });

      instance.jumpToRect(pageIndex, rect);
    },
    [instance, NutrientViewer]
  );

  useEffect(() => {
    if (!instance) return;

    const handlePagePress = (event) => {
      const {pageIndex, point} = event;
      const footing = getFootingAtPoint(pageIndex, point);
      if (footing) {
        scrollToFooting(footing);
        setActiveFooting(footing.id);
      }
    };

    instance.addEventListener('page.press', handlePagePress);

    return () => {
      instance.removeEventListener('page.press', handlePagePress);
    };
  }, [instance, getFootingAtPoint, scrollToFooting, setActiveFooting]);

  return {
    showFootingOverlays,
    setActiveFooting,
    clearFootingOverlays,
    scrollToFooting,
  };
};

export default useFootingOverlays;
